import { AvState, AvStateType } from "@minervaproject/avsubsystem";
import cuid from "cuid";
import { Middleware, Reducer, ReducersMapObject } from "redux";

import { AckReduxPubsubSlice } from "./ack_redux_pubsub_slice";
import { LocalSlice } from "./local_slice";
import { ReduxPubsubSlice } from "./redux_pubsub_slice";
import { PubsubWebSocket } from "./websocket";

import { BaseLogger } from "base_logger";
import { classroomSliceReducer, setUserPubsubConnectionQuality } from "shared/reducers/classroom";
import { monitoringSliceReducer } from "shared/reducers/monitoring";
import {
  AckedActionType,
  ActionMessageType,
  AnnotatedActionFromServerType,
  ClassroomSliceStateType,
  LocalMonitoringSliceStateType,
  LocalSliceStateType,
  MessageFromServerToClientType,
  MonitoringSliceStateType,
  OriginalReducerActionType,
  RawActionFromServerType,
  RequestSyncMessageType,
  StateTypeMap,
} from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { WorkbookSliceStateType } from "shared/types/workbook";
import { initializeStore } from "store";
import { localSliceReducer, setPubsubErrorCode, setPubsubMessage } from "store/reducers/local";
import { localMonitoringSliceReducer } from "store/reducers/local_monitoring";
import { createMonitoringEvent } from "store/reducers/monitoring";
import { workbookSliceReducer } from "store/reducers/workbook";

type LocalSliceMap = {
  local?: LocalSlice<LocalSliceStateType>;
  av?: LocalSlice<AvStateType>;
  workbook?: LocalSlice<WorkbookSliceStateType>;
  local_monitoring?: LocalSlice<LocalMonitoringSliceStateType>;
};

type NonLocalSliceMap = {
  classroom: AckReduxPubsubSlice<ClassroomSliceStateType>;
  monitoring?: ReduxPubsubSlice<MonitoringSliceStateType>;
};

type SliceMap = LocalSliceMap & NonLocalSliceMap;

const PUBSUB_DEBUG = false;

function getAnnotatedAction(
  action: RawActionFromServerType | AckedActionType | RequestSyncMessageType,
  socketType: "classroom" | "monitoring",
): AnnotatedActionFromServerType {
  if (action.type === "ack" || action.type === "sync" || action.type === "request-sync") {
    return {
      ...action,
      meta: {
        socketType,
      },
    };
  }
  return {
    ...action,
    meta: {
      ...action.meta,
      socketType,
    },
  };
}

// ReduxPubsubClient is a wrapper around a Redux store that can handle
// multiple slices of state, each of which can have their own pubsub channel
// and websocket connection. The ReduxPubsubClient constructor takes a map of slice names to
// ReduxPubsubSlice instances.
//
// For example, see getClassroomReduxPubsubClient and getMonitoringReduxPubsubClient below.
export class ReduxPubsubClient extends BaseLogger {
  public store;

  public slices: SliceMap;

  constructor(
    slices: SliceMap,
    subscriptions: (keyof NonLocalSliceMap)[],
    middleware: Middleware[],
  ) {
    super();
    ReduxPubsubClient.log(`Initializing ReduxPubsubClient with subscriptions: ${subscriptions}`);
    this.slices = slices;
    const reducers = Object.fromEntries(
      Object.keys(slices)
        .filter((key) => slices[key as keyof SliceMap] !== undefined)
        .map((key) => [key, slices[key as keyof SliceMap]?.reducer]),
    ) as ReducersMapObject;
    this.store = initializeStore<StateTypeMap>(reducers, middleware);
    this.setUpSubscriptions(subscriptions);
  }

  setUpSubscriptions(subscriptions: (keyof NonLocalSliceMap)[]) {
    ReduxPubsubClient.log(`Setting up subscriptions`);
    subscriptions.forEach((key) => {
      const socket = this.slices[key]?.socket;
      if (socket !== undefined) {
        this.subscribe(socket);
      }
    });
  }

  /*
   * Connect the given websocket to the given store.
   *
   * When a message comes in on the websocket, parse the message and take action
   * on the store.
   *
   * The message.type may be one of the following:
   *
   * - ack: Acknowledgement of message we previously published
   * - action: Informing us of another client's action
   * - sync: Contains a full store hydration payload
   *
   * Returns: Nothing.
   * */
  subscribe(socket: PubsubWebSocket) {
    // serverVersion will get populated by the first SYNC message
    let serverVersion: number;
    let requestSyncInProgress = "";
    let bufferedActionsDict: { [index: number]: ActionMessageType } = {};

    const channel = socket.channel;
    const userId = socket.userId;
    ReduxPubsubClient.log(`User ${userId} is subscribing to channel ${channel}`);

    const _handleActionMessage = (message: ActionMessageType, checkVersion = true) => {
      if (message.action.meta.origin !== "server") {
        ReduxPubsubClient.error("Bad origin:", message);
        return;
      }

      // Handle messages that do not affect the state
      if (message.action.type === "classroom/sendPubsubMessage") {
        ReduxPubsubClient.log("Received pubsub message", message.action.payload);
        this.store.dispatch(setPubsubMessage(message.action.payload));
      }

      if (checkVersion && serverVersion !== message.baseVersion) {
        const firstSync = serverVersion === undefined;
        if (!firstSync && !requestSyncInProgress) {
          requestSyncInProgress = cuid();
          const errorText =
            `Version mismatch, requesting sync from the server (id: ${requestSyncInProgress}). ` +
            `Local: ${serverVersion}, remote: ${message.baseVersion}`;
          ReduxPubsubClient.error(errorText, message);
          this.store.dispatch(
            getAnnotatedAction(
              { type: "request-sync", id: requestSyncInProgress },
              socket.socketType,
            ),
          );
          this.store.dispatch(
            createMonitoringEvent({
              type: MonitoringEventTypeType.PUBSUB_REQUEST_SYNC,
              userId,
              contextData: { message: errorText },
            }),
          );
        } else {
          ReduxPubsubClient.log(
            `Request-sync is already in progress (id: ${requestSyncInProgress}); Buffering message ${message.baseVersion} for later processing`,
          );
          bufferedActionsDict[message.baseVersion] = message;
        }
        return;
      }

      if (message.action.type !== "classroom/sendPubsubMessage") {
        this.store.dispatch(getAnnotatedAction(message.action, socket.socketType));
      }
      serverVersion = message.nextVersion;
    };

    // WebSocket docs:
    // https://developer.mozilla.org/en-US/docs/Web/API/WebSockets_API/Writing_WebSocket_client_applications
    socket.addEventListener("message", (event) => {
      if (PUBSUB_DEBUG) {
        ReduxPubsubClient.log("Received:", event.data);
      }
      const message = JSON.parse(event.data) as MessageFromServerToClientType;
      // Since the classroom page does not subscribe to the monitoring channel, the monitoring channel RTT is not recorded.
      // The monitoring page does subscribe to the classroom and monitoring channels but window.pubsubStatsCollector is not defined.
      // Therefore, only the classroom channel RTT called from the classroom page will be recorded.
      if (message.type === "pong") {
        const rtt = Date.now() - message.startTimestamp;
        window.pubsubStatsCollector?.addPingRecord(rtt);
        const pubsubConnectionQuality =
          this.store.getState().classroom.client.users[userId].pubsub_connection_quality;
        if (pubsubConnectionQuality !== "low" && window.pubsubStatsCollector?.isPingHigh()) {
          ReduxPubsubClient.log(
            `High ping detected for user ${userId}: setting pubsub_connection_quality to "low"`,
          );
          this.store.dispatch(
            setUserPubsubConnectionQuality({ userId, pubsubConnectionQuality: "low" }),
          );
        } else if (pubsubConnectionQuality !== "good" && window.pubsubStatsCollector?.isPingLow()) {
          ReduxPubsubClient.log(
            `Low ping detected for user ${userId}: setting pubsub_connection_quality to "good"`,
          );
          this.store.dispatch(
            setUserPubsubConnectionQuality({ userId, pubsubConnectionQuality: "good" }),
          );
        }
        return;
      }
      if (message.type === "ack") {
        serverVersion = message.nextVersion;
        const { type, psid, accepted } = message;
        this.store.dispatch(getAnnotatedAction({ type, psid, accepted }, socket.socketType));
      } else if (message.type === "action") {
        _handleActionMessage(message);
      } else if (message.type === "sync") {
        const { type, state, version } = message;
        if (PUBSUB_DEBUG) {
          ReduxPubsubClient.log("Sync to version ", version);
        }
        const firstSync = serverVersion === undefined;
        serverVersion = version;
        this.store.dispatch(getAnnotatedAction({ type, state }, socket.socketType));

        if (firstSync || message.requestId === requestSyncInProgress) {
          // Process buffered messages that are newer than the sync version
          const bufferVersions = Object.keys(bufferedActionsDict)
            .map(Number)
            .sort((a, b) => a - b);
          bufferVersions.forEach((bufferVersion) => {
            if (bufferVersion > version) {
              ReduxPubsubClient.log(`Processing buffered message with version ${bufferVersion}`);
              _handleActionMessage(bufferedActionsDict[bufferVersion], false);
            }
          });
          ReduxPubsubClient.log(
            `Finished sync ${message.requestId}: version=${version}, serverVersion: ${serverVersion}`,
          );

          bufferedActionsDict = {};
          requestSyncInProgress = "";
        }
      } else if (message.type === "error") {
        if (message.requestId === requestSyncInProgress) requestSyncInProgress = "";
        ReduxPubsubClient.error(message.message);
        this.store.dispatch(setPubsubErrorCode({ code: message.code }));
      }
    });
  }
}

export function getClassroomReduxPubsubClient(
  classId: string,
  flightAuthToken: string,
  userId: string,
  serverUrl: string,
  monitoringServerUrl: string,
  path?: string,
  actionLogger?: Middleware,
) {
  return new ReduxPubsubClient(
    {
      local: new LocalSlice({ reducer: localSliceReducer }),
      classroom: new AckReduxPubsubSlice({
        reducer: classroomSliceReducer,
        channel: `classroom/${classId}`,
        publishActions: true,
        requestsSync: true,
        flightAuthToken,
        userId,
        origin: "classroom",
        serverUrl,
        monitoringServerUrl,
        path,
      }),
      av: new LocalSlice({ reducer: AvState.avSliceReducer }),
      workbook: new LocalSlice({ reducer: workbookSliceReducer }),
    },
    ["classroom"],
    [actionLogger || window.flightLogger.actionLogger],
  );
}

export type StoreType = ReturnType<typeof getClassroomReduxPubsubClient>["store"];
// The commented definition should be equivalent to the one in shared/types/index.ts
// export type RootState = ReturnType<StoreType["getState"]>;
export type AppDispatch = StoreType["dispatch"];

export function getMonitoringReduxPubsubClient(
  classId: string,
  flightAuthToken: string,
  userId: string,
  serverUrl: string,
  monitoringServerUrl: string,
  path?: string,
) {
  return new ReduxPubsubClient(
    {
      local: new LocalSlice({ reducer: localSliceReducer }),
      local_monitoring: new LocalSlice({ reducer: localMonitoringSliceReducer }),
      classroom: new AckReduxPubsubSlice({
        reducer: classroomSliceReducer,
        channel: `classroom/${classId}`,
        publishActions: false,
        requestsSync: true,
        flightAuthToken,
        userId,
        origin: "monitoring",
        serverUrl,
        monitoringServerUrl,
        path,
      }),
      monitoring: new ReduxPubsubSlice({
        reducer: monitoringSliceReducer as Reducer<
          MonitoringSliceStateType,
          OriginalReducerActionType
        >,
        channel: `monitoring/${classId}`,
        publishActions: false,
        requestsSync: true,
        flightAuthToken,
        userId,
        origin: "monitoring",
        serverUrl: monitoringServerUrl,
        monitoringServerUrl,
        path,
      }),
    },
    ["classroom", "monitoring"],
    [],
  );
}

export type MonitoringStoreType = ReturnType<typeof getMonitoringReduxPubsubClient>["store"];
